import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PaperSection } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Navigation, ArrowLeft, Landmark } from "lucide-react";

const FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='hsl(36,28%,90%)'/><text x='50%' y='50%' font-size='40' fill='hsl(38,52%,46%)' text-anchor='middle' font-family='serif'>神明</text></svg>`
);

export default function DeityDetail() {
  const { id } = useParams();
  const { t, lang, pick } = useI18n();
  const [d, setD] = useState(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    api.get(`/deities/${id}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [id]);

  if (d === null) return <div className="max-w-4xl mx-auto px-4 py-10"><Skeleton className="h-64 rounded-xl" /></div>;
  if (d === false) return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-muted-foreground">Not found</div>;

  const locations = (d.locations && d.locations.length)
    ? d.locations
    : (d.address ? [{ name: pick(d, "name"), address: d.address, lat: d.lat, lng: d.lng }] : []);

  const navUrl = (loc) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.address || pick(d, "name"))}`;
  const embedUrl = (loc) => `https://www.google.com/maps?q=${encodeURIComponent(loc.address || pick(d, "name"))}&output=embed`;
  const cur = locations[selected] || locations[0];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <Link to="/deity" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="deity-back-link">
        <ArrowLeft className="h-4 w-4" /> {t("deityx.backToList")}
      </Link>

      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-2">
          <div className="rounded-2xl overflow-hidden border border-border">
            <AspectRatio ratio={1}>
              <img src={d.image_url || FALLBACK} onError={(e) => { e.currentTarget.src = FALLBACK; }} alt={pick(d, "name")} className="h-full w-full object-cover" />
            </AspectRatio>
          </div>
        </div>
        <div className="md:col-span-3">
          <h1 className="font-display text-3xl font-bold">{pick(d, "name")}</h1>
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-bronze))] font-semibold mt-1">{t("deityx.intro")}</div>
          <p className="mt-3 text-[15px] leading-7 text-foreground/85 whitespace-pre-line">{pick(d, "desc")}</p>
        </div>
      </div>

      {/* Locations */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
          <h2 className="font-display text-xl font-semibold">{t("deityx.locations")}</h2>
        </div>

        {locations.length === 0 ? (
          <div className="text-muted-foreground text-sm">{lang === "zh" ? "暂无地点信息" : "No locations yet"}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              {locations.map((loc, i) => (
                <Card key={i} className={`border-border cursor-pointer ${selected === i ? "ring-2 ring-[hsl(var(--brand-gold))]" : ""}`} onClick={() => setSelected(i)} data-testid={`deity-location-${i}`}>
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold">{loc.name || pick(d, "name")}</div>
                      <div className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{loc.address}</span>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="outline" className="border-[hsl(var(--brand-gold))] shrink-0" data-testid={`deity-navigate-${i}`}>
                      <a href={navUrl(loc)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <Navigation className="h-3.5 w-3.5 mr-1" /> {t("deityx.navigate")}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <PaperSection className="p-3">
              {cur ? (
                <div className="rounded-xl overflow-hidden border border-border h-full">
                  <AspectRatio ratio={4 / 3}>
                    <iframe title="deity-map" src={embedUrl(cur)} className="h-full w-full" frameBorder="0" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="deity-map-iframe" />
                  </AspectRatio>
                </div>
              ) : (
                <div className="h-full min-h-[220px] rounded-xl border border-dashed border-border bg-[hsl(var(--secondary))] p-5 flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">{lang === "zh" ? "暂无地点" : "No location"}</p>
                </div>
              )}
            </PaperSection>
          </div>
        )}
      </div>
    </div>
  );
}
